import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Avatar,
  Chip,
  Button,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  NotificationsActive,
  NotificationsOffOutlined,
  MarkEmailReadOutlined,
  Close,
} from "@mui/icons-material";
import DOMPurify from "dompurify";
import { axiosPrivate } from "../../axios/axios";
import { masterAPI } from "../Masters/dataConfig";
import { timeAgo } from "../../utils/timeAgo";
import { stripHtml } from "../../utils/stripHtml";
import { getNotificationColor, humanizeCategory } from "../../utils/notificationVisual";
import { getNotificationRoute } from "../../utils/notificationRoutes";

const PRIMARY = "#2362B4";

// Renders a notification's raw HTML (often a full email-style document with its
// own <style> block) in a sandboxed iframe so its own styling applies safely,
// without letting it run scripts or touch the parent page's DOM.
const NotificationHtmlFrame = ({ html }) => {
  const [height, setHeight] = useState(120);
  if (!html) return null;

  const sanitized = DOMPurify.sanitize(html, {
    WHOLE_DOCUMENT: true,
    ADD_TAGS: ["style"],
  });

  return (
    <iframe
      title="notification-content"
      srcDoc={sanitized}
      sandbox=""
      style={{ width: "100%", border: "none", height }}
      onLoad={(e) => {
        const doc = e.target.contentDocument;
        if (doc?.body) setHeight(doc.body.scrollHeight + 16);
      }}
    />
  );
};

const groupByDay = (items) => {
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();
  const groups = { Today: [], Yesterday: [], Earlier: [] };

  items.forEach((notif) => {
    const day = notif.createdOn ? new Date(notif.createdOn).toDateString() : null;
    if (day === today) groups.Today.push(notif);
    else if (day === yesterday) groups.Yesterday.push(notif);
    else groups.Earlier.push(notif);
  });

  return Object.entries(groups).filter(([, notifs]) => notifs.length > 0);
};

const NotificationInbox = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notificationItems = useSelector((state) => state.notification.items);
  const unreadItems = notificationItems.filter((notif) => !notif.read);
  const [selectedId, setSelectedId] = useState(null);

  const selected = notificationItems.find((notif) => notif.id === selectedId) || null;

  useEffect(() => {
    const getAllLink = masterAPI["notificationLog"].getall;
    axiosPrivate
      .get(`${getAllLink}`)
      .then((res) => {
        dispatch({ type: "setNotifications", payload: res.data.result });
      })
      .catch((e) => {
        console.log(e, "NotificationLog getall error");
      });
  }, [dispatch]);

  const handleClearAll = () => {
    const unreadIds = notificationItems.filter((it) => !it.read).map((it) => it.id);
    if (unreadIds.length === 0) return;
    const bulkLink = masterAPI["notificationLog"].bulkupdate;
    axiosPrivate
      .put(`${bulkLink}`, unreadIds, { skipSuccessToast: true })
      .then(() => {
        dispatch({ type: "markNotificationsRead", payload: {} });
      })
      .catch((e) => {
        console.log(e, "NotificationLog bulkupdate error");
      });
  };

  const handleOpen = (notif) => {
    if (!notif.read) {
      const bulkLink = masterAPI["notificationLog"].bulkupdate;
      axiosPrivate
        .put(`${bulkLink}`, [notif.id], { skipSuccessToast: true })
        .then(() => {
          dispatch({ type: "markNotificationRead", payload: notif.id });
        })
        .catch((e) => {
          console.log(e, "NotificationLog bulkupdate error");
        });
    }

    const route = getNotificationRoute(notif.notificationType, notif.referenceId);
    if (route) {
      navigate(route);
      return;
    }

    setSelectedId(notif.id);
    const getByIdLink = masterAPI["notificationLog"].getbyid;
    axiosPrivate
      .get(`${getByIdLink}/${notif.id}`)
      .then((res) => {
        dispatch({ type: "updateNotification", payload: res.data.result });
      })
      .catch((e) => {
        console.log(e, "NotificationLog getbyid error");
      });
  };

  const handleClose = () => {
    setSelectedId(null);
  };

  const groupedItems = groupByDay(unreadItems);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 820, mx: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" gap={1.4}>
          <Avatar sx={{ bgcolor: PRIMARY, width: 42, height: 42 }}>
            <NotificationsActive fontSize="small" />
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.25rem", lineHeight: 1.2, color: "#252525" }}>
              Notifications
            </Typography>
            <Typography variant="caption" sx={{ color: "#6C757D" }}>
              {unreadItems.length} unread
            </Typography>
          </Box>
        </Stack>

        {unreadItems.length > 0 && (
          <Tooltip title="Mark everything as read">
            <Button
              size="small"
              startIcon={<MarkEmailReadOutlined fontSize="small" />}
              onClick={handleClearAll}
              sx={{ textTransform: "none", fontWeight: 600, color: PRIMARY }}
            >
              Read all
            </Button>
          </Tooltip>
        )}
      </Stack>

      {unreadItems.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            p: 6,
            borderRadius: 3,
            border: "1px dashed #D9DEE4",
            backgroundColor: "#FAFBFC",
          }}
        >
          <NotificationsOffOutlined sx={{ fontSize: 42, color: "#B0B8C1" }} />
          <Typography sx={{ color: "#6C757D" }}>You're all caught up</Typography>
          <Typography variant="caption" sx={{ color: "#ADB5BD" }}>
            New notifications will show up here
          </Typography>
        </Paper>
      ) : (
        groupedItems.map(([label, notifs]) => (
          <Box key={label} sx={{ mb: 2.5 }}>
            <Typography
              sx={{
                px: 0.5,
                mb: 1,
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "#98A2B3",
                textTransform: "uppercase",
                letterSpacing: 0.6,
              }}
            >
              {label}
            </Typography>

            <Paper
              elevation={0}
              sx={{ borderRadius: 3, border: "1px solid #EAEDF1", overflow: "hidden" }}
            >
              {notifs.map((notif, idx) => (
                <React.Fragment key={notif.id}>
                  <Box
                    onClick={() => handleOpen(notif)}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.6,
                      p: 1.8,
                      cursor: "pointer",
                      backgroundColor: "#F7FAFD",
                      transition: "background-color 0.15s ease",
                      "&:hover": { backgroundColor: "#EEF4FC" },
                    }}
                  >
                    <Avatar sx={{ bgcolor: getNotificationColor(notif.category), width: 38, height: 38, mt: 0.2 }}>
                      <NotificationsActive fontSize="small" />
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography sx={{ fontWeight: 700, fontSize: "0.92rem" }}>
                            {notif.title}
                          </Typography>
                          {notif.category && (
                            <Chip
                              label={humanizeCategory(notif.category)}
                              size="small"
                              sx={{ height: 20, fontSize: "0.7rem", backgroundColor: "#EAF1FB", color: PRIMARY }}
                            />
                          )}
                        </Stack>
                        {notif.createdOn && (
                          <Typography
                            variant="caption"
                            sx={{ color: "#ADB5BD", whiteSpace: "nowrap", flexShrink: 0 }}
                          >
                            {timeAgo(notif.createdOn)}
                          </Typography>
                        )}
                      </Stack>

                      <Typography variant="body2" noWrap sx={{ color: "#495057", mt: 0.3 }}>
                        {stripHtml(notif.message)}
                      </Typography>
                    </Box>
                  </Box>
                  {idx < notifs.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Paper>
          </Box>
        ))
      )}

      <Dialog open={Boolean(selected)} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Avatar sx={{ bgcolor: getNotificationColor(selected?.category), width: 32, height: 32 }}>
              <NotificationsActive fontSize="small" />
            </Avatar>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
              {selected?.title || "Notification"}
            </Typography>
          </Stack>
          <IconButton size="small" onClick={handleClose}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <NotificationHtmlFrame html={selected?.message} />
          {selected?.createdOn && (
            <Typography variant="caption" sx={{ color: "#ADB5BD", mt: 1.5, display: "block" }}>
              {timeAgo(selected.createdOn)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationInbox;
